import * as Sentry from '@sentry/react'

import { useCallback, useEffect, useState } from 'react'

import { APIError } from '@/lib/asyncThunk'
import { actions } from '@/slices/auth'
import { useAppDispatch } from '@/store'

export default function useWebex() {
  const dispatch = useAppDispatch()
  const [error, setError] = useState<APIError<unknown>>()

  const handleToken = useCallback(
    async (accessToken: string) => {
      try {
        const response = await fetch('/api/webex/authenticate', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ accessToken }),
        })

        if (response.status >= 400 && response.status < 500) {
          const resp = (await response.json()) as APIError<unknown>
          setError(resp)
          return
        }

        const { token } = await response.json()
        dispatch(actions.setJWT(token))
      } catch (err) {
        Sentry.captureException(err)
        console.error('authenticate with webex error', err)
        setError({
          error: (err as Error).message,
          statusCode: 0,
        })
      }
    },
    [dispatch]
  )

  useEffect(() => {
    if (!window.isWebex) return

    const params = new URLSearchParams(window.location.search)
    const token = params.get('token')
    if (!token) {
      console.error('No auth token was passed from Webex')
      return
    }

    const retryWithBackoff = async (fn: () => Promise<void>, maxDelay = 16_000) => {
      let delay = 250
      while (delay <= maxDelay) {
        try {
          await fn()
          return
        } catch {
          await new Promise(resolve => setTimeout(resolve, delay))
          delay *= 2
        }
      }
      await fn()
    }

    retryWithBackoff(() => handleToken(token))
  }, [handleToken])

  return [window.isWebex, error] as const
}
